/* IBM Confidential‌ - OCO Source Materials‌ - (C) COPYRIGHT IBM CORP. 2022-2024 - The source code for this program is not published or otherwise‌ divested of its trade secrets, irrespective of what has been‌ deposited with the U.S. Copyright Office. */

import {
  Button,
  ComposedModal,
  InlineNotification,
  ModalBody,
  ModalFooter,
  ModalHeader,
  TextInput,
} from "carbon-components-react";
import React, { Component } from "react";
import {
  fetchMillisToExpire,
  isSessionAboutToExpire,
  getMillisBeforeExpiration,
  updateUserSession,
  SESSION_WARNING_THRESHOLD,
  userHasActiveSession,
  checkUserSessionStatus,
  isSSO,
  isIgnoreTimeout,
  ouathProfileName,
  handleCurrentContextUser
} from "../utils/UserSession";
import { AppMsg } from "../utils";
import { updateCsrfToken } from '../services/CsrfTokenService';
import "./SessionExpiredModal.scss";

//SSO Auth handling variables
var ssoAuthWin;
var handleSSOSessionCheck;

const SESSION_RELOAD_CHECK_NEEDED = true;

class SessionExpiredModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showWarningModal: false,
      showLoginModal: false,
      warningErrorMessage: "",
      loginErrorMessage: "",
      isSSO: isSSO(),
      ouathProfileName: ouathProfileName(),
      username: this.props.username,
      password: "",
    };
    this.loginModalTimeout = null;
    this.checkSession = null;
    this.loginObj = props.loginObj;
    this.languageId = props.languageId;
    this.pwdTextBox = React.createRef();
    
    //Set the SSO Auth session check variable function.
    handleSSOSessionCheck = this.ssoSessionCheck;

  }

  login = () => {
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        userName: this.state.username,
        password: this.state.password,
      }),
    };

    let authenticationUrl = this._handleAuthenticationUrl();

    fetch(authenticationUrl, requestOptions).then((response) =>
      this.handleResponse(response)
    );
  };

  _handleAuthenticationUrl = () => {
    return (
      "../../p/websignon/tririga-login" +
      "?screenWidth=" +
      this.loginObj.screenWidth +
      "&screenHeight=" +
      this.loginObj.screenHeight +
      "&screenDPI=" +
      this.loginObj.screenDPI +
      "&contentType=" +
      this.loginObj.contentType +
      "&languageId=" +
      this.languageId +
      "&ieFlag=" +
      this.loginObj.ieFlag
    );
  };

  handleResponse = (response) => {
    if (!response.ok) {
      if (response.status === 401) {
        const error = AppMsg.getMessage(AppMsg.INVALID_CREDENTIALS);
        this.setState({ loginErrorMessage: error });
      } else {
        const error =
          AppMsg.getMessage(AppMsg.APP_ERROR_MSG) +
          " " +
          AppMsg.getMessage(AppMsg.APP_ERROR) +
          ":" +
          response.status;
        this.setState({ loginErrorMessage: error });
      }
    } else {
      updateCsrfToken(); //Update csrf token.
      this.setState({ 
        showLoginModal: false, 
        password: ""
      });
      document.querySelector(".projectContainerDiv").style.filter = "none";
      this.checkTimeout();
    }
  };

  checkTimeout = () => {
    fetchMillisToExpire().then(() => {
      // If timeout should be ignored then return right away.
      if (isIgnoreTimeout()) {
        return;
      }
      let interval = getMillisBeforeExpiration();

      this.setState({ showWarningModal: isSessionAboutToExpire() });

      if (
        !this.state.showWarningModal &&
        interval >= SESSION_WARNING_THRESHOLD
      ) {
        setTimeout(this.checkTimeout, interval - SESSION_WARNING_THRESHOLD);
        clearTimeout(this.loginModalTimeout);
      } else {
        this.loginModalTimeout = setTimeout(() => {
          checkUserSessionStatus().then(() => {
            if (!userHasActiveSession()) {
              this.displayLoginPrompt();
              // A work-around to notify wxo when the user login session is expired
              // TODO: Remove this custom firing event. This firing event will not needed 
              // once the Platform has an endpoint that returns JSESSION or JWT with expired duration.
              const event = new Event('tri-notify-wxo-session-expired');
              document.dispatchEvent(event);
            } else {
              this.checkTimeout();
            }
          });
        }, interval);
      }
    });
  };

  updateSession = () => {
    updateUserSession()
      .then((status) => {
        this.setState({ showWarningModal: false });
        if (status === 401) {
          // Display login prompt 
          this.displayLoginPrompt();
        } else {
          this.checkTimeout();
        }
      })
      .catch((error) => {
        this.setState({ warningErrorMessage: error.message });
      });
  };

  displayLoginPrompt = () => {
    document.querySelector(".projectContainerDiv").style.filter =
    "blur(8px)";
    this.setState({ showLoginModal: true, showWarningModal: false });
  }

  ssoLogin = () => {
      const left = (window.screen.width - 657) / 2;
      const top = (window.screen.height - 500) / 4;
      //Only open sso auth window if not already open.
      if (!ssoAuthWin || ssoAuthWin.closed) {
          let url = "../../login";
          if( this.state.ouathProfileName && this.state.ouathProfileName !== "" ) {
              url = `../../p/oauth/${this.state.ouathProfileName}/tririga`;
          }
          ssoAuthWin = window.open(
              url,
              "TririgaSSOAuthWin",
              "dependant=no,location=no,menubar=no,toolbar=no,directories=no,status=yes,scrollbars=auto,resizable=no,height=500,width=657,top=" +
              top +
              ",left=" +
              left
          );
      }
  };

  ssoSessionCheck = (isSessionReloadCheckNeeded) => {
    checkUserSessionStatus().then(() => {
      if (userHasActiveSession()) {
        updateCsrfToken(); //Update csrf token.
        this.setState({ showLoginModal: false });
        document.querySelector(".projectContainerDiv").style.filter = "none";
        clearTimeout(this.checkSession);
        this.checkTimeout();
        //Now handle session reload if needed.
        if (isSessionReloadCheckNeeded && isSessionReloadCheckNeeded === true) {
          this.validateUserSession().then(isSessionReloadNeeded => {
            if (isSessionReloadNeeded) {
              window.location.reload();
            }
          });
        }
      }
    });
  }

  validateUserSession() {
    //First get the current user from client.
    let currentUserName = this.props.username;

    //Now compare current user from server with current client user.
    return handleCurrentContextUser().then(currentContextUser => {
      return currentContextUser !== currentUserName;
    });
  }

  componentDidMount() {
    this.checkTimeout();
    document.addEventListener("keydown", this.handleEscKey); 
  }

  componentDidUpdate() {
    if (this.state.showLoginModal && !this.state.isSSO) {
      this._handleLoginModalFocus();
    }
  }

  _handleLoginModalFocus() {
    let pwdTextBox = document.querySelector("#password");
      if (pwdTextBox) {
        pwdTextBox.focus();
      }
  }

  componentWillUnmount(){
    document.removeEventListener("keydown", this.handleEscKey);
  }

  handleEscKey = (event) => {
    if (event.keyCode === 27) {
      //Close Warning Model if it is showing
      if (this.state.showWarningModal) {
        this.setState({ showWarningModal: false });
        this.props.handleMultiSessionLogout();
      }
      //Close Login Model if it is showing
      if (this.state.showLoginModal  && this.state.isSSO) {
        this.setState({ showLoginModal: false });
            document.querySelector(".projectContainerDiv").style.filter =
              "none";
            this.props.doLogout();
      }
       //Close Login Model if it is showing
       if (this.state.showLoginModal  && !this.state.isSSO) {
        this.setState({ showLoginModal: false });
        document.querySelector(".projectContainerDiv").style.filter =
          "none";
        this.props.doLogout();
      }
    }
  }

  _handleLoginEnterKey = (event) => {
    if (event.keyCode === 13) {
      //Ignore if inline notfication close button.
      if (event.target.className === "bx--inline-notification__close-button") {
        return;
      }
      if (this.state.password.length > 0 &&
          this.state.loginErrorMessage.length === 0) {
            this.login();
          }
    }
  }

  render() {
    return this.state.showWarningModal ? (
      <ComposedModal
        selectorPrimaryFocus=".bx--btn--primary"
        size="xs"
        preventCloseOnClickOutside={true}
        passiveModal
        onClose={this.updateSession}
        iconDescription={AppMsg.getMessage(AppMsg.CLOSE)}
        open
        type="normal"
        className="modal"
        containerClassName="container"
      >
        <ModalHeader
          closeClassName="bx-close-model-custom"
        >{AppMsg.getMessage(AppMsg.SESSION_TIMEOUT)}</ModalHeader>
        <ModalBody>
          {AppMsg.getMessage(AppMsg.SESSION_TIMEOUT_MSG)}
          {this.state.warningErrorMessage ? (
            <div>
              <InlineNotification
                hideCloseButton={false}
                iconDescription={AppMsg.getMessage(AppMsg.CLOSE)}
                kind="error"
                notificationType="inline"
                role="alert"
                statusIconDescription={AppMsg.getMessage(AppMsg.APP_ERROR)}
                subtitle={this.state.warningErrorMessage}
                title={AppMsg.getMessage(AppMsg.APP_ERROR)}
                lowContrast
              />
            </div>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button
            kind="secondary"
            onClick={() => {
              this.setState({ showWarningModal: false });
              this.props.handleMultiSessionLogout();
            }}
          >
            {AppMsg.getMessage(AppMsg.APP_LOGOUT)}
          </Button>
          <Button kind="primary" onClick={this.updateSession}>
            {AppMsg.getMessage(AppMsg.SESSION_RENEW)}
          </Button>
        </ModalFooter>
      </ComposedModal>
    ) : this.state.showLoginModal && this.state.isSSO ? (
      <ComposedModal
        selectorPrimaryFocus=".bx--btn--primary"
        size="sm"
        preventCloseOnClickOutside={true}
        passiveModal
        onClose={this.props.doLogout}
        iconDescription={AppMsg.getMessage(AppMsg.CLOSE)}
        open
        type="normal"
        className="modal"
        containerClassName="container"
      >
        <ModalHeader>{AppMsg.getMessage(AppMsg.SESSION_EXPIRED)}</ModalHeader>
        <ModalBody>{AppMsg.getMessage(AppMsg.SESSION_EXPIRE_MSG)}</ModalBody>
        <ModalFooter>
           <Button
            kind="secondary"
            onClick={() => {
              this.setState({ showLoginModal: false });
              document.querySelector(".projectContainerDiv").style.filter =
                "none";
              this.props.doLogout();
            }}
          >
            {AppMsg.getMessage(AppMsg.APP_SWITCH_USER)}
          </Button>
          <Button kind="primary" onClick={this.ssoLogin}>
            {AppMsg.getMessage(AppMsg.SESSION_LOGIN)}
          </Button>
        </ModalFooter>
      </ComposedModal>
    ) : this.state.showLoginModal && !this.state.isSSO ? (
      <ComposedModal
        size="sm"
        preventCloseOnClickOutside={true}
        passiveModal
        onClose={this.props.doLogout}
        iconDescription={AppMsg.getMessage(AppMsg.CLOSE)}
        open
        type="normal"
        className="modal"
        containerClassName="container"
        onKeyDown={this._handleLoginEnterKey}
      >
        <ModalHeader>{AppMsg.getMessage(AppMsg.LOGIN_MSG)}</ModalHeader>
        <ModalBody hasForm>
          <TextInput
            defaultValue={this.state.username}
            disabled
            id="username"
            labelText={AppMsg.getMessage(AppMsg.APP_USERNAME)}
            type="text"
          />

          <TextInput
            data-modal-primary-focus
            labelText={AppMsg.getMessage(AppMsg.APP_PASSWORD)}
            id="password"
            placeholder={AppMsg.getMessage(AppMsg.PASSWORD_PLACEHOLDER)}
            type="password"
            onChange={(e) => this.setState({ password: e.target.value })}
            autoComplete="new-password"
            ref={this.pwdTextBox}
          />
          {this.state.loginErrorMessage ? (
            <div>
              <InlineNotification
                hideCloseButton={false}
                iconDescription={AppMsg.getMessage(AppMsg.CLOSE)}
                kind="error"
                notificationType="inline"
                role="alert"
                statusIconDescription={AppMsg.getMessage(AppMsg.APP_ERROR)}
                subtitle={this.state.loginErrorMessage}
                title={AppMsg.getMessage(AppMsg.APP_ERROR)}
                onCloseButtonClick={() => {
                    this.pwdTextBox.current.focus();
                    this.setState({ loginErrorMessage: "" })
                  }
                }
                lowContrast
              />
            </div>
          ) : null}
        </ModalBody>
        <ModalFooter
          primaryButtonText={AppMsg.getMessage(AppMsg.APP_LOGIN)}
          primaryButtonDisabled={
            this.state.password.length === 0 ||
            this.state.loginErrorMessage.length > 0
          }
          onRequestSubmit={this.login}
          secondaryButtonText={AppMsg.getMessage(AppMsg.APP_SWITCH_USER)}
          onRequestClose={() => {
            this.setState({ showLoginModal: false });
            document.querySelector(".projectContainerDiv").style.filter =
              "none";
            this.props.doLogout();
          }}
        ></ModalFooter>
      </ComposedModal>
    ) : null;
  }
}

export default SessionExpiredModal;

//Handling of SSO Auth window after it authenticates. 
window.closeSSOAuthWin = () => {
  if (ssoAuthWin) {
    ssoAuthWin.close();
    handleSSOSessionCheck(SESSION_RELOAD_CHECK_NEEDED);
  }
}
